import React from 'react';
import cn from 'classnames';
import { Tile, TileHeader } from '@creditflowresearch/uilib/Tile';
import Title from '@creditflowresearch/uilib/Title';
import Flex from '@creditflowresearch/uilib/Flex';
import Button from '@creditflowresearch/uilib/Button';
import TraceTable from '@/creditflowresearch/ui/components/trace/common/TraceTable';
import ComposeTraceTileContext from '@/creditflowresearch/application/Trace/ComposeTraceTileContext';
import { useParentPageContext } from '@/creditflowresearch/application/ParentPageContext';
import { useModuleVisibility } from '@/creditflowresearch/application/ModuleVisibility';
import { TRACE_TILE_HEADER_MAPPING_NAME } from '@/creditflowresearch/application/Bonds/constants';
import TraceChart from '@/creditflowresearch/ui/components/trace/TraceChart';
import TraceChartContextProvider from '@/creditflowresearch/application/Trace/TraceChartContext';
import cfrUrl from '@/creditflowresearch/infrastructure/url';
import EmptyMessageWithLink from '@/creditflowresearch/ui/common/components/EmptyMessageWithLink';
import SearchInput from '@/creditflowresearch/ui/components/trace/common/SearchInput';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import tileStyles from '@creditflowresearch/uilib/Tile/tile.module.scss';

export interface ITraceTileProps {
  withChart?: boolean,
  className?: string,
}

const TraceTile = ({ withChart = false, className }: ITraceTileProps) => {
  const isModuleVisible = useModuleVisibility(TRACE_TILE_HEADER_MAPPING_NAME);

  if (!isModuleVisible) return null;

  return (
    <Tile dataTest="traceTile" className={ cn(spacesStyles.mtl, className) }>
      <TileHeader
        title={ <Title title="TRACE" dataTest="traceChartTitle" /> }
        dataTest="traceTileHeader"
      >
        <Flex
          direction={ { sm: 'column-reverse', md: 'column-reverse', xl: 'row' } }
          align={ { sm: 'stretch', md: 'stretch', xl: 'center' } }
          gap={ 10 }
        >
          <SearchInput size="S" className={ tileStyles.headerSearch } />
          <Button
            as="a"
            href={ cfrUrl.getTraceUrl() }
            variant="outline"
            size="S"
            dataTest="goToTraceLink"
          >
            All Trades
          </Button>
        </Flex>
      </TileHeader>
      { withChart && <TraceChart /> }
      <TraceTable
        isItemsPerPageVisible={ false }
        emptyMessage={ (
          <EmptyMessageWithLink
            text="Explore all other"
            linkTo={ cfrUrl.getTraceUrl() }
            linkTitle="TRACE"
          />
        ) }
      />
    </Tile>
  );
};

export default ({ withChart, className }: ITraceTileProps) => {
  const { traceTileData } = useParentPageContext();

  return (
    <ComposeTraceTileContext traceTileData={ traceTileData }>
      <TraceChartContextProvider withChart={ withChart }>
        <TraceTile withChart={ withChart } className={ className } />
      </TraceChartContextProvider>
    </ComposeTraceTileContext>
  );
};
